import { Email, LTLShipment, FTLShipment } from "@src/types/api";
import { emailShipmentsFtl, emailShipmentsLtl } from "@taskpane/utils/emailShipments";
import type { LoadApplication } from "@src/load-agent/LoadApplication";
import { statusHistoryIdentityPayload } from "@src/load-agent/office/resolveActorEmailForThread";

export interface OpenReplyDraftOptions {
  selectedIds: Set<string>;
  emailData: Email | null;
  internetMessageId?: string;
  refreshEmailData?: () => Promise<unknown>;
  onError?: (message: string) => void;
  htmlBodyOverride?: string;
  updateLoadStatuses?: boolean;
}

function outlookMailboxUserEmail(): string | undefined {
  try {
    const mailbox = Office.context?.mailbox as
      | {
          userProfile?: {
            emailAddress?: string;
          };
        }
      | undefined;
    const addr = mailbox?.userProfile?.emailAddress;

    return typeof addr === "string" && addr.trim() !== "" ? addr.trim() : undefined;
  } catch {
    return undefined;
  }
}

type LoadAppReply = Pick<
  LoadApplication,
  | "postLoadAgentReplyDraftHtml"
  | "recordEmailInteractionLead"
  | "updateLTLLoadStatus"
  | "updateFTLLoadStatus"
>;

type ReplyCallbackContext = {
  loadApp: LoadAppReply;
  internetMessageId: string;
  emailData: Email | null;
  selectedLTL: LTLShipment[];
  selectedFTL: FTLShipment[];
  updateLoadStatuses: boolean;
  refreshEmailData?: () => Promise<unknown>;
  onError?: (message: string) => void;
};

async function runAfterReplyOpened(ctx: ReplyCallbackContext): Promise<void> {
  const {
    loadApp,
    internetMessageId,
    emailData,
    selectedLTL,
    selectedFTL,
    updateLoadStatuses,
    refreshEmailData,
  } = ctx;
  const actorEmail = outlookMailboxUserEmail();
  const tasks: Promise<unknown>[] = [];

  if (actorEmail) {
    tasks.push(loadApp.recordEmailInteractionLead({ internetMessageId, actorEmail }));
  }
  if (updateLoadStatuses) {
    const identityPayload = statusHistoryIdentityPayload(emailData);

    tasks.push(
      ...selectedLTL.map((s) =>
        loadApp.updateLTLLoadStatus({
          shipmentId: s.shipmentId,
          internetMessageId,
          loadStatus: "Quote Sent",
          ...identityPayload,
        })
      ),
      ...selectedFTL.map((s) =>
        loadApp.updateFTLLoadStatus({
          shipmentId: s.shipmentId,
          internetMessageId,
          loadStatus: "Quote Sent",
          ...identityPayload,
        })
      )
    );
  }
  if (tasks.length > 0) {
    try {
      await Promise.all(tasks);
    } catch (error) {
      void error;
    }
  }
  if (refreshEmailData) {
    try {
      await refreshEmailData();
    } catch (error) {
      void error;
    }
  }
}

async function resolveReplyHtmlBody(
  loadApp: LoadAppReply,
  options: OpenReplyDraftOptions,
  item: Office.MessageRead
): Promise<string | undefined> {
  const { htmlBodyOverride, internetMessageId, selectedIds, onError } = options;

  if (htmlBodyOverride) return htmlBodyOverride;
  if (!internetMessageId?.trim()) {
    onError?.("internetMessageId is required to build the reply.");
    return undefined;
  }
  const senderNameFromOutlook =
    (
      item as {
        from?: {
          displayName?: string;
        };
      }
    ).from?.displayName ||
    (
      item as {
        sender?: {
          displayName?: string;
        };
      }
    ).sender?.displayName ||
    "";

  try {
    return await loadApp.postLoadAgentReplyDraftHtml({
      internetMessageId: internetMessageId.trim(),
      shipmentIds: [...selectedIds],
      prospectDisplayName: senderNameFromOutlook || undefined,
    });
  } catch (e) {
    onError?.(e instanceof Error ? e.message : "Failed to build reply.");
    return undefined;
  }
}

export function runOpenReplyDraftFlow(loadApp: LoadAppReply, options: OpenReplyDraftOptions): void {
  void (async () => {
    const {
      selectedIds,
      emailData,
      internetMessageId,
      refreshEmailData,
      onError,
      updateLoadStatuses = true,
    } = options;

    try {
      const item = Office.context?.mailbox?.item;

      if (!item) {
        onError?.("No email item found.");
        return;
      }
      const selectedLTL = emailData
        ? emailShipmentsLtl(emailData).filter((s) => selectedIds.has(s.shipmentId))
        : [];
      const selectedFTL = emailData
        ? emailShipmentsFtl(emailData).filter((s) => selectedIds.has(s.shipmentId))
        : [];
      const htmlBody = await resolveReplyHtmlBody(loadApp, options, item as Office.MessageRead);

      if (!htmlBody) return;
      const isOWA =
        (Office.context?.mailbox as { diagnostics?: { hostName?: string } })?.diagnostics
          ?.hostName === "OutlookWebApp";
      const replyMethod = isOWA
        ? (
            item as {
              displayReplyFormAsync: typeof item.displayReplyAllFormAsync;
            }
          ).displayReplyFormAsync
        : item.displayReplyAllFormAsync;
      const callbackCtx: ReplyCallbackContext = {
        loadApp,
        internetMessageId: internetMessageId ?? "",
        emailData,
        selectedLTL,
        selectedFTL,
        updateLoadStatuses,
        refreshEmailData,
        onError,
      };

      replyMethod.call(item, { htmlBody }, async (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          onError?.("Failed to open reply: " + asyncResult.error.message);
          return;
        }
        if (!internetMessageId) {
          return;
        }
        await runAfterReplyOpened(callbackCtx);
      });
    } catch {
      onError?.("An error occurred while opening the reply.");
    }
  })();
}
